import { type ChangeEvent, useEffect, useState, useRef } from 'react'
import { TextFieldWithLabel } from 'Components/TextField'
import { sendGet, sendPost, sendPut } from 'hooks/use-fetch'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { Box, Button, CircularProgress, Container, DialogActions, Grid, Tooltip, Typography, type SelectChangeEvent } from '@mui/material'
import { type postError, type lookup, type PortalChildSummary } from 'core/types'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { type CaregiverMemberDetails } from 'Pages/CaregiverPortal/Members/CaregiverDisplay'
import { generatePath, useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { CAREGIVER_PORTAL_MEMBERS, CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS } from 'Pages/Routes'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { type ChildRelationship } from 'Pages/CaregiverPortal/Members/ChildRelationships'
import { AsYouType } from 'libphonenumber-js'
import { RaceSelection } from 'Pages/CaregiverPortal/Members/RaceSelection'
import { useAuth } from 'hooks/use-auth'
import { BackArrow } from 'Assets/SVGs/back-arrow'
import { RelationshipCard } from 'Components/Cards/CaregiverPortal/RelationshipCard'
import { RelationshipEdit } from 'Pages/CaregiverPortal/Members/RelationshipEdit'

const newCaregiver: CaregiverMemberDetails = {
  id: 0,
  isdid: 0,
  firstName: '',
  middleName: null,
  lastName: '',
  dateOfBirth: null,
  primaryPhone: '',
  secondaryPhone: '',
  email: '',
  preferredContactMethodID: null,
  preferredContactMethod: '',
  educationLevelID: null,
  highestEducationLevel: '',
  languageID: null,
  primaryLanguage: '',
  locationID: 0,
  location: null,
  livesWithCaregiverID: null,
  raceList: [],
  isHispanic: null,
  tribalAffiliationStatus: null,
  tribalAffiliationID: null,
  hasPortalAccess: false,
  userID: null,
  childRelationships: []
}

export function CaregiverEdit (): JSX.Element {
  const { caregiverId } = useParams()
  const isNew = useRef(caregiverId === 'new')
  const [isLoading, setIsLoading] = useState(true)
  const [languages, setLanguages] = useState<lookup[]>([])
  const [caregiver, setCaregiver] = useState<CaregiverMemberDetails>(newCaregiver)
  // name as loaded, so the title doesn't change while the name fields are being edited
  const [initialName, setInitialName] = useState('')
  const [contactMethods, setContactMethods] = useState<lookup[]>([])
  const [relationships, setRelationships] = useState<ChildRelationship[]>([])
  const [modifyingRelationship, setModifyingRelationship] = useState<ChildRelationship | null>(null)
  const [relationshipErrors, setRelationshipErrors] = useState<postError[]>([])
  const nextTempRelationshipId = useRef(-1)
  const [errors, setErrors] = useState<postError[]>([])
  const [submitting, setSubmitting] = useState(false)
  const nav = useNavigate()
  const auth = useAuth()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!isNew.current) {
        const { response, success } = await sendGet(`/CaregiverPortal/GetCaregiverDetails/${caregiverId ?? 0}`, {})
        if (success) {
          const loaded: CaregiverMemberDetails = response
          setCaregiver(loaded)
          setInitialName(`${loaded.firstName ?? ''} ${loaded.lastName ?? ''}`.trim())
        } else {
          toast.error('Failed to load caregiver details')
        }

        // relationship rows are keyed/labeled from the CHILD's side here (caregiverID/caregiverName hold the child's
        // id/name) so they can be rendered with the same RelationshipCard/RelationshipEdit ChildEdit.tsx uses; the
        // real (constant) caregiver id is carried in childID instead, and swapped back at submit time.
        const { response: relRsp } = await sendGet(`/CaregiverPortal/GetCaregiverRelationships/${caregiverId ?? 0}`, {})
        if (relRsp != null) {
          setRelationships(relRsp.map((r: any) => ({
            caregiverID: r.childID,
            caregiverName: r.caregiverName,
            relationshipID: r.relationshipID,
            financiallyResponsibleFor: r.financiallyResponsibleFor,
            livesWithChild: r.livesWithChild,
            childID: r.caregiverID,
            id: r.id,
            relationshipName: r.relationshipName
          })))
        }
      } else {
        // one not-yet-related row per child in the network; where the caregiver lives is expressed through the
        // Lives With flag on these relationships (caregivers hold no address of their own)
        const { response: relRsp } = await sendGet('/CaregiverPortal/GetRelatedMembers', {})
        if (relRsp != null) {
          const howRelatedList: ChildRelationship[] = []
          relRsp.children.forEach((c: PortalChildSummary) => {
            howRelatedList.push({
              caregiverID: c.id,
              caregiverName: `${c.firstName} ${c.lastName}`,
              relationshipID: null,
              financiallyResponsibleFor: null,
              livesWithChild: false,
              id: null,
              childID: 0,
              relationshipName: null
            })
          })
          setRelationships(howRelatedList)
        }
      }
      const { response: langs, error: err1 } = await sendGet('/Language/GetLookup?sort=true', {})
      if (err1[0] === '') {
        setLanguages(langs)
      }
      const { response: contactmethods, error: err2 } = await sendGet('/ContactMethod/GetAll', {})
      if (err2[0] === '') {
        setContactMethods(contactmethods)
      }
      setIsLoading(false)
    }
    void fetchData()
  }, [])

  const returnToDetails = (): void => {
    let route: string
    if (isNew.current) {
      route = generatePath(CAREGIVER_PORTAL_MEMBERS)
    } else {
      route = generatePath(CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS, { caregiverId: caregiverId ?? '' })
    }
    nav(route)
  }

  const handleChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setCaregiver({ ...caregiver, [e.target.name]: e.target.value })
  }

  const handleDateChange = (name: string, newDate: Date | null): void => {
    setCaregiver({ ...caregiver, [name]: newDate })
  }

  const handleSelectChange = (e: SelectChangeEvent<number | null>): void => {
    setCaregiver({ ...caregiver, [e.target.name]: e.target.value })
  }

  // errors for these fields are shown beneath the field itself; anything else falls back to the list at the top
  const inlineErrorFields = ['firstName', 'lastName', 'dateOfBirth', 'email', 'primaryPhone', 'races', 'isHispanic', 'relationships']
  const fieldError = (name: string): string | undefined => errors.find(e => e.field === name)?.error
  const summaryErrors = errors.filter(e => !inlineErrorFields.includes(e.field))

  const availableRelationships = relationships.filter(r => r.id === null)

  const hasUnsavedSubEdits = modifyingRelationship != null

  const handleRelationshipSubmit = (data: ChildRelationship): void => {
    // local only - not saved server-side until the caregiver record is saved
    setRelationshipErrors([])
    const isNewlyConfigured = data.id === null
    const finalized: ChildRelationship = isNewlyConfigured ? { ...data, id: nextTempRelationshipId.current-- } : data
    const isMatch = (r: ChildRelationship): boolean => isNewlyConfigured ? (r.id === null && r.caregiverID === data.caregiverID) : r.id === data.id
    if (relationships.some(isMatch)) {
      setRelationships(relationships.map(r => (isMatch(r) ? finalized : r)))
    } else {
      setRelationships([...relationships, finalized])
    }
    setModifyingRelationship(null)
  }

  const handleDeleteRelationship = (caregiverID: number): void => {
    // local only - reverts a not-yet-saved pending relationship back to "available to add"
    setRelationships(relationships.map(r => r.caregiverID === caregiverID
      ? { ...r, id: null, relationshipID: null, financiallyResponsibleFor: null, livesWithChild: false }
      : r))
    setModifyingRelationship(null)
  }

  const validateRelationships = (): postError[] => {
    const related = relationships.filter(r => r.relationshipID !== null)
    if (related.length === 0) {
      return [{ field: 'relationships', error: 'Child Relationship is required' }]
    }
    return []
  }

  const handleSubmit = async (): Promise<void> => {
    if (!submitting) {
      setSubmitting(true)
      // relationships are saved in a separate call after the caregiver record, so they're validated here - together
      // with the server's field validation, so every error is shown at once and nothing is saved until all pass
      const validation = await sendPost('/CaregiverPortal/ValidateCaregiver', caregiver)
      const allErrors: postError[] = [...(validation.response?.errors ?? []), ...validateRelationships()]
      if (!validation.success || allErrors.length > 0) {
        setErrors(allErrors)
        setSubmitting(false)
        window.scrollTo(0, 0)
        return
      }

      let rsp: { success: boolean, errors: postError[], newRecordID: number | null }
      if (isNew.current) {
        const result = await sendPost('/CaregiverPortal/InsertCaregiver', caregiver)
        rsp = result.response
        if (rsp.newRecordID !== null && result.success) {
          const relationshipsToSubmit = relationships.map(r => ({
            id: (r.id != null && r.id < 0) ? null : r.id,
            childID: r.caregiverID,
            caregiverID: rsp.newRecordID as number,
            relationshipID: r.relationshipID,
            financiallyResponsibleFor: r.financiallyResponsibleFor,
            livesWithChild: r.livesWithChild,
            caregiverName: r.caregiverName,
            relationshipName: r.relationshipName,
            hasPortalAccess: caregiver.hasPortalAccess
          }))
          await sendPost('/CaregiverPortal/UpdateChildRelationships', relationshipsToSubmit)
        }
      } else {
        caregiver.location = null
        const result = await sendPut('/CaregiverPortal/UpdateCaregiverDetails', caregiver)
        rsp = result.response
        if (result.success) {
          const relationshipsToSubmit = relationships.map(r => ({
            id: (r.id != null && r.id < 0) ? null : r.id,
            childID: r.caregiverID,
            caregiverID: parseInt(caregiverId ?? '0'),
            relationshipID: r.relationshipID,
            financiallyResponsibleFor: r.financiallyResponsibleFor,
            livesWithChild: r.livesWithChild,
            caregiverName: r.caregiverName,
            relationshipName: r.relationshipName,
            hasPortalAccess: caregiver.hasPortalAccess
          }))
          await sendPost('/CaregiverPortal/UpdateChildRelationships', relationshipsToSubmit)
        }
      }

      if (rsp.success) {
        if (isNew.current) {
          const route = generatePath(CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS, { caregiverId: rsp?.newRecordID?.toString() ?? '' })
          nav(route)
        } else {
          returnToDetails()
        }
      } else {
        setSubmitting(false)
        window.scrollTo(0, 0)
        setErrors(rsp.errors)
      }
    }
  }

  if (isLoading) {
    return <CircularProgress />
  } else {
    return <Container sx={{ mb: '24px' }}>
      <Box sx={{ mt: '24px', mb: '24px', display: 'flex', alignItems: 'center' }}>
        <Box onClick={returnToDetails} data-testid='backArrow' sx={{ cursor: 'pointer', display: 'flex' }}>
          <BackArrow />
        </Box>
        <Typography variant='h5' sx={{ fontWeight: 600, ml: '5px' }} component='div'>
          {isNew.current ? 'New Caregiver' : `Edit Caregiver${initialName !== '' ? ` - ${initialName}` : ''}`}
        </Typography>
      </Box>

      <Box >
        {summaryErrors.length > 0 &&
          <ul>
            {summaryErrors.map((err, i) => (
              <li key={`${err.field ?? ''}-${i}`} style={{ color: 'red', padding: '10px 0 0' }}>
                {err.error}
              </li>
            ))}
          </ul>
        }
        <Grid container columns={6} spacing={1}>
          <Grid item xs={6}>
            <TextFieldWithLabel label='First Name' required={true} showRequiredText={fieldError('firstName') != null} error={fieldError('firstName') != null} helperText={fieldError('firstName')} data-testid="firstName" name='firstName' maxLength={50} value={caregiver?.firstName ?? ''} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Middle Name' data-testid="middleName" name='middleName' maxLength={50} value={caregiver?.middleName ?? ''} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Last Name' required={true} showRequiredText={fieldError('lastName') != null} error={fieldError('lastName') != null} helperText={fieldError('lastName')} data-testid="lastName" name='lastName' maxLength={50} value={caregiver?.lastName ?? ''} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <DatePickerWithLabel label='Date of Birth' error={fieldError('dateOfBirth') != null} helperText={fieldError('dateOfBirth')} onChange={(newValue) => { handleDateChange('dateOfBirth', newValue) }} value={caregiver?.dateOfBirth ?? null} name='dateOfBirth' />
          </Grid>
          <Grid item xs={6}>
            <SelectWithLabel
              name="languageID"
              label="Primary Language (if not English)"
              width="100%"
              includeNoneOption={true}
              value={caregiver.languageID ?? undefined}
              options={languages}
              onChange={handleSelectChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Email' data-testid="email" name='email' required={true} showRequiredText={fieldError('email') != null} error={fieldError('email') != null} helperText={fieldError('email')} maxLength={100} value={caregiver?.email ?? ''} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <CheckBoxWithLabel label='Caregiver Portal Access' edge='start' name='hasPortalAccess'
              value={caregiver.hasPortalAccess}
              disabled={caregiver.userID === auth.user?.id}
              setValue={(val) => { setCaregiver({ ...caregiver, hasPortalAccess: val }) }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Phone Number' error={fieldError('primaryPhone') != null} helperText={fieldError('primaryPhone')} data-testid="primaryPhone" name='primaryPhone' maxLength={50} value={new AsYouType('US').input(caregiver?.primaryPhone ?? '')} onChange={handleChange} />
          </Grid>
          <Grid item xs={6} >
            <SelectWithLabel
              name="preferredContactMethodID"
              label="Preferred Contact Method"
              width="100%"
              value={caregiver.preferredContactMethodID ?? undefined}
              options={contactMethods}
              onChange={handleSelectChange}
            />
          </Grid>
          <Grid item xs={6} >
            <RaceSelection
              raceList={caregiver.raceList ?? []}
              isHispanic={caregiver.isHispanic}
              required={true}
              raceError={fieldError('races')}
              ethnicityError={fieldError('isHispanic')}
              setRaceList={(raceList, isHispanic) => {
                setCaregiver(prev => ({ ...prev, raceList, isHispanic }))
              }}
              tribalAffiliationStatus={caregiver.tribalAffiliationStatus ?? false}
              tribalAffiliationID={caregiver.tribalAffiliationID}
              setTribeStatus={(checked, tribeId) => {
                setCaregiver(prev => ({ ...prev, tribalAffiliationStatus: checked, tribalAffiliationID: tribeId }))
              }}
              tribeTooltipText='This is the tribal affiliation of the caregiver'
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: '20px' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', mb: '8px' }}>Child Relationships</Typography>
          {fieldError('relationships') != null && (
            <Typography variant='caption' color='error' display='block' data-testid='relationshipsError' sx={{ mb: '8px' }}>{fieldError('relationships')}</Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {relationships.filter(r => r.id !== null).map(r => (
              modifyingRelationship?.caregiverID === r.caregiverID
                ? <RelationshipEdit
                    isChild
                    key={r.caregiverID}
                    relationship={modifyingRelationship}
                    childName={modifyingRelationship.caregiverName}
                    errors={relationshipErrors}
                    onCancel={() => { setModifyingRelationship(null); setRelationshipErrors([]) }}
                    onSubmit={(data) => { handleRelationshipSubmit(data) }}
                    onDelete={(modifyingRelationship.id ?? 0) < 0 ? () => { handleDeleteRelationship(r.caregiverID) } : undefined}
                />
                : <RelationshipCard
                    key={r.caregiverID}
                    relationship={r}
                    childName={r.caregiverName}
                    onModify={() => { setModifyingRelationship(r) }}
                />
            ))}
          </Box>
          {modifyingRelationship != null && modifyingRelationship.id === null && (
            <Box sx={{ mt: '12px' }}>
              <RelationshipEdit
                isChild
                key={modifyingRelationship.caregiverID}
                relationship={modifyingRelationship}
                childName={modifyingRelationship.caregiverName}
                errors={relationshipErrors}
                candidates={availableRelationships}
                onSwitchCandidate={(candidate) => { setModifyingRelationship(candidate) }}
                onCancel={() => { setModifyingRelationship(null); setRelationshipErrors([]) }}
                onSubmit={(data) => { handleRelationshipSubmit(data) }}
              />
            </Box>
          )}
          {availableRelationships.length > 0 && !(modifyingRelationship != null && modifyingRelationship.id === null) && (
            <Typography
              onClick={() => {
                setModifyingRelationship(availableRelationships.length === 1
                  ? availableRelationships[0]
                  : { ...availableRelationships[0], caregiverID: 0, caregiverName: '' })
              }}
              data-testid='addRelationship'
              sx={{ fontWeight: 600, fontSize: '16px', color: '#222024', textDecoration: 'underline', cursor: 'pointer', textAlign: 'center', mt: '12px' }}
            >
              {`+Add Pending Relationship (${availableRelationships.length})`}
            </Typography>
          )}
        </Box>

        <DialogActions sx={{ justifyContent: 'space-between', gap: '8px' }}>
          <Button
            data-testid='cancelButton'
            variant='outlined'
            onClick={returnToDetails}
            sx={{ flex: 1, borderRadius: '50px', borderColor: '#969696', color: '#222024', textTransform: 'none', py: '16px' }}
          >
            Discard Changes
          </Button>

          <Tooltip title={hasUnsavedSubEdits ? 'Finish or cancel the relationship changes you\'re editing before saving' : ''}>
            <span style={{ flex: 1 }}>
              <Button
                data-testid='updateButton'
                onClick={handleSubmit}
                variant='contained'
                fullWidth
                disabled={submitting || hasUnsavedSubEdits}
                sx={{ borderRadius: '50px', backgroundColor: '#0E8476', textTransform: 'none', py: '16px', '&:hover': { backgroundColor: '#0b6c60' } }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Box>
    </Container>
  }
}
